import { Box, IconButton, Tooltip } from "@mui/material";
import { SxProps } from '@mui/system';
import React, { useState, useEffect } from "react";
import { CustomTable, TableFooter, TableHeaderToolbar } from "../CustomTable";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface RowObj {
    id: string | number;
    is_linked: string;
}

interface Props<T extends RowObj> {
    rows: T[];
    title?: string;
    sx?: SxProps;
    currentPage: number;
    pageSize: number;
    count: number;
    pageSizeOptions: number[];
    loading?: boolean;
    columns?: string[];
    columnsMap?: Record<string, string>;
    valuesMap?: Record<string, (value: any) => any>
    onPageChange(newPage: number): void;
    onPageSizeChange(newPageSize: number): void;
    onEditSelected: (obj: T) => void;
    onDeleteSelected: (obj: T[]) => void;
    onLinkLicense: (obj: T) => void;
    setLinkLicenseIcon?: boolean;
    SetindexToZero?: boolean;
    setDownloadLicenseIcon?: boolean;
    onDownloadLicense: (obj: T) => void;
    setSeeMoreLicenseIcon?: boolean;
    onSeeMoreLicense: (obj: T) => void;
    setHideEditButton?: boolean
    onLinkSelected: (obj: T) => void;
    setLinkIcon?: boolean
    setSeeMoreIcon?: boolean
    onSeeMore: (obj: T) => void;
    handleSeeDetails: (obj: string | number | T) => void;
}

export const ActionTable = <T extends RowObj,>(props: Props<T>) => {
    const {
        rows,
        columns,
        columnsMap,
        valuesMap,
        currentPage,
        pageSize,
        count,
        loading = false,
        title = "",
        pageSizeOptions,
        onPageChange,
        onPageSizeChange,
        onEditSelected,
        onDeleteSelected,
        onLinkLicense,
        setLinkLicenseIcon,
        setDownloadLicenseIcon,
        onDownloadLicense,
        setSeeMoreLicenseIcon,
        onSeeMoreLicense,
        setHideEditButton,
        onLinkSelected,
        SetindexToZero,
        setSeeMoreIcon,
        setLinkIcon,
        handleSeeDetails,
    } = props;

    const [selectedRows, setSelectedRows] = useState<T[]>([]);
    
    const handleEditClick = () => {
        if (selectedRows.length === 1) {
            onEditSelected(selectedRows[0])
            setSelectedRows([])
        }
    }
    const handleLinkClick = () => {
        if (selectedRows.length === 1) {
            onLinkLicense(selectedRows[0])
            setSelectedRows([])
        }
    }
    const handleDownloadLicenseClick = () => {
        if (selectedRows.length === 1) {
            onDownloadLicense(selectedRows[0])
        }
    }
    const handleSeeMoreLicense = () => {
        if (selectedRows.length === 1) {
            onSeeMoreLicense(selectedRows[0])
        }
    }
    const selectAllClick = () => {
        if (selectedRows.length === rows.length) {
            setSelectedRows([])
        } else {
            setSelectedRows(rows)
        }
    }

    const handleRemoveClick = () => {
        if (selectedRows.length > 0) {
            onDeleteSelected(selectedRows)
        }
        setSelectedRows([])
    }

    const handleLinkLicenseClick = () => {
        if (selectedRows.length === 1) {
            onLinkSelected(selectedRows[0])
        }
    }

    const handleSeeDetailsOnClick = () => {
        if (selectedRows.length > 0) {
            handleSeeDetails(selectedRows[0])
        }       
    }

    const onCheckboxSelected = (isChecked: boolean, row: T, key: React.Key) => {
        if (!isChecked) {
            setSelectedRows([row, ...selectedRows])
        } else {
            setSelectedRows([...selectedRows.filter((c) => c.id !== row.id)])
        }
    }
    useEffect(() => {
        if (SetindexToZero) {
            setSelectedRows([])
        }
    }, [SetindexToZero])

    return (
        <Box>
            <TableHeaderToolbar title={title} numSelected={selectedRows.length}>
                <>
                    {(selectedRows.length === 1 && setSeeMoreIcon === true) ?
                            <Tooltip title="Veja mais">
                                <IconButton aria-label="See item details" onClick={handleSeeDetailsOnClick}>
                                    <VisibilityIcon />
                                </IconButton>
                            </Tooltip>
                            :
                            null
                        }
                        {(selectedRows.length === 1 && setLinkIcon === true) ?
                            <Tooltip title="Vincular Hardware e Licença">
                                <IconButton aria-label="Link hardware" onClick={handleLinkLicenseClick} >
                                    <InsertLinkIcon />
                                </IconButton>
                            </Tooltip>
                            :
                            null
                        }
                        {selectedRows.length === 1 && setDownloadLicenseIcon ? (
                            <Tooltip title="Fazer Download da Licença">
                                <IconButton aria-label="link company" onClick={handleDownloadLicenseClick}>
                                    <DownloadIcon />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                        {selectedRows.length === 1 && setLinkLicenseIcon && selectedRows[0].is_linked==='Não Vinculada' ? (
                            <Tooltip title="Vincular">
                                <IconButton aria-label="link company" onClick={handleLinkClick}>
                                    <InsertLinkIcon />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                        {selectedRows.length === 1 && setSeeMoreLicenseIcon ? (
                            <Tooltip title="Ver mais">
                                <IconButton aria-label="See more about it" onClick={handleSeeMoreLicense}>
                                    <VisibilityIcon />
                                </IconButton>
                            </Tooltip>
                        ) : null}


                        {selectedRows.length === 1 && setHideEditButton !== false ? (
                            <Tooltip title="Editar">
                                <IconButton aria-label="edit item" onClick={handleEditClick}>
                                    <EditIcon />
                                </IconButton>

                            </Tooltip>
                        ) : null}


                        {selectedRows.length > 0 && (
                            <Tooltip title="Remover">
                                <IconButton aria-label="delete items" onClick={handleRemoveClick}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                    )}
                </>
            </TableHeaderToolbar>
            <CustomTable
                rows={rows}
                selected={selectedRows}
                enableCheck={true}
                columns={columns}
                columnsMap={columnsMap}
                valuesMap={valuesMap}
                loading={loading}
                onCheckboxClick={onCheckboxSelected}
                onSelectAllClick={selectAllClick}
            />
            <TableFooter
                totalRows={count}
                rowsPerPage={pageSize}
                page={currentPage}
                rowsPerPageOptions={pageSizeOptions || [10, 25, 40]}
                onPageChanged={onPageChange}
                onRowsPerPageChanged={onPageSizeChange}
            />
        </Box>
    )
}